import { memo, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material';
import { useQuery } from 'react-query';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormTextField from 'components/ui/form/FormTextField';
import useFormSubmit from 'hooks/useFormSubmit';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useSearch from 'hooks/useSearch';

const ShareWidgets = ({ open, setOpen, refetchFn }) => {
  const query = useSearch();
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);

  const { submit, isSubmitting } = useFormSubmit();

  const formik = useFormik({
    initialValues: {
      filter_name: '',
      users: [],
    },
    // validationSchema: validationSchema,
    onSubmit: async (values) => {
      const body = {
        dashboard_id: query.get('dashboard'),
        users: values?.users?.filter((u) => u?.type)?.map(({ id }) => ({ id })),
      };
      submit(
        { type: 'post', contentType: 'simple' },
        body,
        '/admin/widgets/share',
        {
          title: t('dashboard.addedWidget'),
        },
        null,
        true,
        handleFinish
      );
    },
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: 'singleBlockFilterSelect',
    queryFn: async function () {
      const response = await axiosPrivate.get('/dictionary/staffes');
      return response.data.data;
    },
    enabled: !hasError,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const handleFinish = () => {
    refetchFn();
    handleClose();
  };

  useEffect(() => {
    if (data?.length) {
      const users = data?.map((d) => ({
        id: d?.id,
        name: d?.name,
        type: false,
      }));
      formik.setFieldValue('users', users);
    }
  }, [data, isLoading, isFetching]);

  const onChange = (type, item) => {
    const newData = formik?.values?.users?.map((u) =>
      u?.id == item?.id ? { ...u, type } : u
    );
    formik?.setFieldValue('users', newData);
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      scroll='body'
      PaperProps={{
        className: '!shadow-none !base-border',
      }}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        id='alert-dialog-title'
        className='text-center bg-gray-100 !`mb-2'
      >
        {/* <span className='pr-5'>{t('common.button.addNew')}</span> */}
        <span>Ulashish</span>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit} className='pt-2 h-full'>
          <Grid
            container
            spacing={{ xs: 2, sm: 2, md: 2 }}
            rowSpacing={1}
            columns={{ xs: 12, sm: 12, md: 12 }}
            className='!h-full'
          >
            <Grid item={true} md={12} sm={12} xs={12}>
              <FormTextField
                delay={0.1}
                label={t('common.fields.simpleName')}
                fieldName='filter_name'
                formik={formik}
              />
            </Grid>
            <div className='!h-[calc(100%-200px)] max-h-[500px] grow my-scroll  overflow-auto w-full px-4'>
              <Grid item={true} md={12} sm={12} xs={12}>
                {isLoading || isFetching ? (
                  <div className='circular-progress-box'>
                    <CircularProgress size={25} />
                  </div>
                ) : formik?.values?.users &&
                  formik?.values?.users.length > 0 ? (
                  formik?.values?.users
                    ?.map((a, i) => ({ ...a, i }))
                    ?.filter((a) =>
                      a?.name
                        ?.toUpperCase()
                        ?.includes(formik?.values?.filter_name?.toUpperCase())
                    )
                    ?.map((item, index) => (
                      <label
                        className={
                          'grid grid-cols-12 py-1 hover:bg-gray-100 cursor-pointer items-center'
                        }
                        key={`block-${index + 1}`}
                      >
                        <div className='col-span-12 font-semibold flex items-center text-[14px] gap-2'>
                          <Checkbox
                            checked={!!item?.type}
                            onChange={(e) => onChange(e?.target?.checked, item)}
                          />
                          <div className='min-w-10 uppercase w-10 h-10 flex items-center justify-center rounded-full bg-gray-200'>
                            {item?.name?.[0]}
                          </div>
                          {item?.name}
                        </div>
                      </label>
                    ))
                ) : (
                  <div>
                    <span className='no-data-found-wrapper select-box'>
                      <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
                      {t('common.global.noDataFound')}
                    </span>
                  </div>
                )}
              </Grid>
            </div>

            <Grid item={true} sm={12} xs={12}>
              <FormActionButtons
                delay={0.3}
                isSubmitting={isSubmitting}
                formType='dialog'
                setOpen={setOpen}
                disabled={
                  !formik?.values?.users?.filter((u) => u?.type)?.length
                }
                reset={formik.resetForm}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(ShareWidgets);
